import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Address, Hex } from "viem";
import { useAccount, useSignTypedData, useSwitchChain } from "wagmi";
import { getGearboxApiUrl } from "@/utils/api-fetch";
import { canSignAsAuditor } from "@/utils/audit-permissions";
import { useWeb3 } from "./use-web3";

export function useAddAudit({ onSuccess }: { onSuccess: () => void }): {
  canSign: boolean;
  sign: (bytecodeHash: Hex, reportUrl: string) => Promise<void>;
} {
  const { address, chainId } = useAccount();
  const { bytecodeRepository } = useWeb3();

  const { switchChain } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();

  const [auditors, setAuditors] = useState<Address[]>();

  useEffect(() => {
    const fetchAuditors = async () => {
      if (!bytecodeRepository) return;

      const auditors = await bytecodeRepository.getAuditors();
      setAuditors(auditors.map(a => a.toLowerCase()) as Address[]);
    };

    // Only fetch once when component mounts and bytecodeRepository is available
    fetchAuditors();
  }, [bytecodeRepository]); // Only re-run if bytecodeRepository changes

  const { mutateAsync } = useMutation({
    mutationFn: async (data: {
      auditor: Address;
      signature: Hex;
      bytecodeHash: Hex;
      reportUrl: string;
    }) => {
      const baseUrl = getGearboxApiUrl();

      const response = await fetch(`${baseUrl}/bcr/audit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to validate signature");
      }

      return response.json();
    },
  });

  const canSign = canSignAsAuditor(address, auditors);

  const sign = async (bytecodeHash: Hex, reportUrl: string) => {
    if (!address) return;
    if (chainId !== 1) {
      switchChain({ chainId: 1 });
    }

    if (!bytecodeRepository) return;

    const signProposalTypeData = bytecodeRepository.computeEIP712AuditorDigest(
      bytecodeHash,
      address,
      reportUrl,
    );

    const signature = await signTypedDataAsync(signProposalTypeData);

    try {
      await mutateAsync({
        signature,
        bytecodeHash,
        reportUrl,
        auditor: address,
      });
      onSuccess();
    } catch (error) {
      console.error("Error validating signature:", error);
      throw error;
    }
  };

  return { canSign, sign };
}
